import { PERMISSIONS } from '@repo/contracts';
import { lazy } from 'react';
import type { RouteObject } from 'react-router';

import { AppShell } from '@/app/app-shell';
import { APP_ROUTES } from '@/constants/app-routes';

import { HomeRedirect } from './home-redirect';
import { RequireAuth } from './require-auth';
import { RequirePermission } from './require-permission';

const LoginPage = lazy(() => import('@/pages/login/LoginPage'));
const NotFoundPage = lazy(() => import('@/pages/not-found/NotFoundPage'));
const ApplicationsPage = lazy(() => import('@/pages/applications/ApplicationsPage'));
const ApplicationDetailPage = lazy(
  () => import('@/pages/application-detail/ApplicationDetailPage'),
);
const PersonPage = lazy(() => import('@/pages/person/PersonPage'));
const NewPersonPage = lazy(() => import('@/pages/new-person/NewPersonPage'));
const MyAccessPage = lazy(() => import('@/pages/my-access/MyAccessPage'));
const PeoplePage = lazy(() => import('@/pages/people/PeoplePage'));
const RouteErrorPage = lazy(() => import('@/pages/route-error/RouteErrorPage'));

export const routes: RouteObject[] = [
  { path: APP_ROUTES.login, element: <LoginPage />, errorElement: <RouteErrorPage /> },
  {
    element: <RequireAuth />,
    // Last resort: only reached if AppShell itself fails to render, so it
    // renders RouteErrorPage without the sidebar chrome that just broke.
    errorElement: <RouteErrorPage />,
    children: [
      {
        element: <AppShell />,
        children: [
          {
            // A pathless layout route so the boundary sits inside AppShell:
            // an error here keeps the sidebar, instead of errorElement on
            // AppShell itself replacing the whole shell.
            errorElement: <RouteErrorPage />,
            children: [
              { path: APP_ROUTES.home, element: <HomeRedirect /> },
              // Open to every signed-in person: RequireAuth lets it past the no-access gate.
              { path: APP_ROUTES.myAccess, element: <MyAccessPage /> },
              {
                element: <RequirePermission permission={PERMISSIONS.userRead} />,
                children: [
                  { path: APP_ROUTES.people, element: <PeoplePage /> },
                  { path: APP_ROUTES.person(':userId'), element: <PersonPage /> },
                ],
              },
              {
                element: <RequirePermission permission={PERMISSIONS.roleRead} />,
                children: [
                  { path: APP_ROUTES.applications, element: <ApplicationsPage /> },
                  { path: APP_ROUTES.application(':key'), element: <ApplicationDetailPage /> },
                ],
              },
              {
                element: <RequirePermission permission={PERMISSIONS.userCreate} />,
                children: [{ path: APP_ROUTES.newPerson, element: <NewPersonPage /> }],
              },
            ],
          },
          // Last child, so it never shadows a real route above it.
          { path: '*', element: <NotFoundPage /> },
        ],
      },
    ],
  },
];
